import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileText, Sparkles, User, Link2, Search } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import GapReportBanner from "@/components/GapReportBanner";
import { ConfidenceBadge } from "@/components/BidIntakeBadges";

const STATUS_LABEL = {
    uploaded: "Uploaded",
    extracting: "Extracting files…",
    parsing_specs: "Parsing spec book…",
    ready: "Ready",
    converted: "Converted",
    failed: "Failed",
};

export default function BidIntakeDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [clientName, setClientName] = useState("");
    const [jobAddress, setJobAddress] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get(`/bid-intake/jobs/${id}`);
            setJob(data);
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        load();
        // Poll while still processing
        const t = setInterval(() => {
            setJob((cur) => {
                if (!cur) return cur;
                if (["uploaded", "extracting", "parsing_specs"].includes(cur.status)) {
                    load();
                }
                return cur;
            });
        }, 2500);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [id]);

    const generate = async () => {
        if (!clientName.trim()) {
            setErr("Enter a client/project name before generating the pre-estimate.");
            return;
        }
        setBusy(true);
        setErr("");
        try {
            const { data } = await api.post(`/bid-intake/jobs/${id}/pre-estimate`, {
                client_name: clientName.trim(),
                job_address: jobAddress.trim(),
                service_type: "interior",
            });
            navigate(`/estimates/${data.estimate_id}`);
        } catch (e) {
            setErr(apiErrorMessage(e, "Could not generate pre-estimate"));
        } finally {
            setBusy(false);
        }
    };

    if (!job) {
        return (
            <div className="page-shell">
                <p className="text-muted">Loading…</p>
            </div>
        );
    }

    const paintSystems = job.extracted_data?.paint_systems || [];

    return (
        <div className="page-shell">
            <Link
                to="/admin/bid-intake"
                className="link-red inline-flex items-center gap-1 text-sm"
                data-testid="bid-detail-back"
            >
                <ArrowLeft size={14} />
                Back to bid intake
            </Link>

            <header className="fade-up mt-4 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <p className="eyebrow">Bid intake job</p>
                    <h1 className="h1 mt-3" data-testid="bid-detail-filename">
                        {job.original_filename}
                    </h1>
                    <p className="text-muted text-sm mt-2">
                        Status:{" "}
                        <strong>{STATUS_LABEL[job.status] || job.status}</strong>
                        {" · "}
                        Uploaded {fmtDateTime(job.created_at)}
                        {job.completed_at && ` · Completed ${fmtDateTime(job.completed_at)}`}
                    </p>
                </div>
                {job.pre_estimate_id && (
                    <Link
                        to={`/estimates/${job.pre_estimate_id}`}
                        className="btn btn-primary"
                        data-testid="bid-detail-open-estimate"
                    >
                        <FileText size={14} />
                        Open pre-estimate
                    </Link>
                )}
            </header>

            {err && (
                <div
                    className="brand-card mt-4"
                    style={{
                        borderColor: "var(--brand-red)",
                        color: "var(--brand-red)",
                    }}
                    data-testid="bid-detail-error"
                >
                    {err}
                </div>
            )}

            {job.error && (
                <div
                    className="brand-card mt-4"
                    style={{
                        borderColor: "var(--brand-red)",
                        color: "var(--brand-red)",
                    }}
                    data-testid="bid-detail-failed"
                >
                    <strong>Pipeline failed:</strong> {job.error}
                </div>
            )}

            <div className="mt-5">
                <GapReportBanner gapReport={job.gap_report} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
                <div className="lg:col-span-2 flex flex-col gap-5">
                    <div className="brand-card fade-up">
                        <p className="eyebrow">Extracted paint systems</p>
                        {paintSystems.length === 0 ? (
                            <p className="text-muted mt-3 text-sm">
                                No paint systems extracted yet.
                            </p>
                        ) : (
                            <div className="overflow-x-auto mt-3">
                                <table className="brand-table" data-testid="paint-systems-table">
                                    <thead>
                                        <tr>
                                            <th>Substrate</th>
                                            <th>Primer</th>
                                            <th>Finish</th>
                                            <th className="text-right">Coats</th>
                                            <th>Source</th>
                                            <th>Confidence</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paintSystems.map((ps, i) => (
                                            <tr key={i} data-testid={`paint-system-${i}`}>
                                                <td className="capitalize">
                                                    {(ps.substrate || "").replace("_", " ")}
                                                </td>
                                                <td className="text-sm">
                                                    {ps.primer || "—"}
                                                </td>
                                                <td className="text-sm">
                                                    {ps.finish}
                                                    {ps.finish_type ? (
                                                        <span className="text-xs text-muted">
                                                            {" · "}
                                                            {ps.finish_type}
                                                        </span>
                                                    ) : null}
                                                </td>
                                                <td className="text-right font-mono">
                                                    {ps.finish_coats || 0}
                                                </td>
                                                <td className="text-xs text-muted">
                                                    §{ps.source_section} · p.{ps.source_page}
                                                </td>
                                                <td>
                                                    <ConfidenceBadge
                                                        confidence={ps.confidence}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="brand-card fade-up delay-1">
                        <p className="eyebrow">File breakdown</p>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-3">
                            {Object.entries(job.categories || {}).map(([k, n]) => (
                                <div
                                    key={k}
                                    className="rounded-md p-3"
                                    style={{ background: "rgba(0,0,0,0.03)" }}
                                    data-testid={`category-${k}`}
                                >
                                    <p className="text-xs text-muted uppercase tracking-wide">
                                        {k}
                                    </p>
                                    <p className="font-display font-bold text-2xl mt-1">
                                        {n}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <aside className="flex flex-col gap-5">
                    <LinkLeadCard job={job} onLinked={load} />
                    <div className="brand-card fade-up">
                        <p className="eyebrow">Generate pre-estimate</p>
                        {job.status !== "ready" && !job.pre_estimate_id && (
                            <p className="text-muted text-sm mt-2">
                                Available once extraction is complete.
                            </p>
                        )}
                        {job.pre_estimate_id && (
                            <p className="text-muted text-sm mt-2">
                                Already generated — open the pre-estimate above.
                            </p>
                        )}
                        {job.status === "ready" && !job.pre_estimate_id && (
                            <>
                                <label className="field-label mt-3">Client / project name</label>
                                <input
                                    className="field-input"
                                    value={clientName}
                                    onChange={(e) => setClientName(e.target.value)}
                                    placeholder="Highline at Silver Creek"
                                    data-testid="bid-detail-client"
                                />
                                <label className="field-label mt-3">Job address</label>
                                <input
                                    className="field-input"
                                    value={jobAddress}
                                    onChange={(e) => setJobAddress(e.target.value)}
                                    placeholder="123 Silver Creek Rd, Park City UT"
                                    data-testid="bid-detail-address"
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary w-full mt-4"
                                    onClick={generate}
                                    disabled={busy}
                                    data-testid="bid-detail-generate-btn"
                                >
                                    <Sparkles size={14} />
                                    {busy ? "Generating…" : "Generate pre-estimate"}
                                </button>
                                <p className="text-xs text-muted mt-2">
                                    Creates a draft estimate with paint systems pre-populated.
                                    Quantities and unit prices stay manual.
                                </p>
                            </>
                        )}
                    </div>
                </aside>
            </div>
        </div>
    );
}

function LinkLeadCard({ job, onLinked }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [busy, setBusy] = useState(false);
    const [linking, setLinking] = useState(null);
    const [err, setErr] = useState("");

    const search = async (e) => {
        e?.preventDefault?.();
        if (!q.trim()) return;
        setBusy(true);
        setErr("");
        try {
            const { data } = await api.get("/admin/leads", { params: { q: q.trim() } });
            setResults((data.leads || []).slice(0, 6));
        } catch (e2) {
            setErr(apiErrorMessage(e2));
        } finally {
            setBusy(false);
        }
    };

    const link = async (leadId) => {
        setLinking(leadId);
        setErr("");
        try {
            await api.post(`/bid-intake/jobs/${job.id}/link-lead`, { lead_id: leadId });
            onLinked?.();
            setResults([]);
            setQ("");
        } catch (e) {
            setErr(apiErrorMessage(e, "Could not link lead"));
        } finally {
            setLinking(null);
        }
    };

    if (job.lead_id && job.lead_snapshot) {
        return (
            <div className="brand-card fade-up" data-testid="bid-detail-lead-linked">
                <p className="eyebrow">Linked lead</p>
                <Link
                    to={`/leads/${job.lead_id}`}
                    className="mt-2 flex items-center gap-2 link-red font-semibold"
                    data-testid="bid-detail-lead-link"
                >
                    <User size={14} />
                    {job.lead_snapshot.name}
                </Link>
                {job.lead_snapshot.property_address && (
                    <p className="text-xs text-muted mt-1">
                        {job.lead_snapshot.property_address}
                    </p>
                )}
            </div>
        );
    }

    return (
        <div className="brand-card fade-up" data-testid="bid-detail-link-lead-card">
            <p className="eyebrow">Link to lead</p>
            <p className="text-muted text-sm mt-1">
                Attach this bid to an existing lead so the pre-estimate inherits contact info
                and the lead auto-advances to &quot;quoted&quot;.
            </p>
            <form onSubmit={search} className="mt-3 flex gap-2">
                <div className="relative flex-1">
                    <Search
                        size={13}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--brand-muted)]"
                    />
                    <input
                        type="text"
                        className="field-input"
                        style={{ paddingLeft: "1.9rem" }}
                        placeholder="Search name, email, phone"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="link-lead-search-input"
                    />
                </div>
                <button
                    type="submit"
                    className="btn btn-secondary"
                    disabled={busy || !q.trim()}
                    data-testid="link-lead-search-btn"
                >
                    {busy ? "…" : "Find"}
                </button>
            </form>
            {err && (
                <p
                    className="text-xs mt-2"
                    style={{ color: "var(--brand-red)" }}
                    data-testid="link-lead-error"
                >
                    {err}
                </p>
            )}
            {results.length > 0 && (
                <ul className="mt-3 flex flex-col gap-2" data-testid="link-lead-results">
                    {results.map((l) => (
                        <li
                            key={l.id}
                            className="flex items-center justify-between gap-2 rounded-md p-2"
                            style={{ background: "rgba(0,0,0,0.03)" }}
                        >
                            <div className="min-w-0">
                                <p className="text-sm font-semibold truncate">{l.name}</p>
                                <p className="text-xs text-muted truncate">
                                    {l.email} · {l.phone}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-primary text-xs shrink-0"
                                disabled={linking === l.id}
                                onClick={() => link(l.id)}
                                data-testid={`link-lead-${l.id}`}
                            >
                                <Link2 size={11} />
                                {linking === l.id ? "Linking…" : "Link"}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
